'use client'

import Link from 'next/link'

export interface BreadcrumbItem {
  label: string
  href?: string
}

interface BreadcrumbsProps {
  items: BreadcrumbItem[]
  /** Light variant for dark backgrounds, dark variant for light backgrounds */
  variant?: 'light' | 'dark'
}

/**
 * Breadcrumbs navigation — clean, minimal, accessible.
 * Uses semantic <nav> + aria-label + schema-friendly structure.
 */
export default function Breadcrumbs({ items, variant = 'dark' }: BreadcrumbsProps) {
  const textColor = variant === 'light' ? 'text-white/70' : 'text-gray-500'
  const hoverColor = variant === 'light' ? 'hover:text-white' : 'hover:text-forest'
  const activeColor = variant === 'light' ? 'text-white/50' : 'text-gray-400'
  const chevronColor = variant === 'light' ? 'text-white/30' : 'text-gray-300'

  return (
    <nav
      className={`flex items-center gap-1.5 text-sm ${textColor} flex-wrap`}
      aria-label="Breadcrumb"
    >
      <ol className="flex items-center gap-1.5 flex-wrap list-none m-0 p-0">
        {items.map((item, i) => {
          const isLast = i === items.length - 1
          return (
            <li key={i} className="flex items-center gap-1.5">
              {i > 0 && (
                <svg
                  className={`w-3 h-3 ${chevronColor} flex-shrink-0`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              )}
              {isLast || !item.href ? (
                <span className={`${activeColor} truncate max-w-[200px]`} aria-current="page">
                  {item.label}
                </span>
              ) : (
                <Link
                  href={item.href}
                  className={`${hoverColor} transition-colors duration-150`}
                >
                  {item.label}
                </Link>
              )}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}
